import Dexie, { Table } from "dexie";
import { action, makeObservable, observable, override } from "mobx";
import { DateTime } from "luxon";

import { MemCommonEditorContentUnderstandingInfo, assetProxyModule } from "@mem-labs/common-editor";

import { Maybe } from "@/domains/common/types";
import { Uuid } from "@/domains/global/identifiers";
import { AppStore } from "@/store/AppStore";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { OptimisticSyncUpdate, SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import {
  SpaceAccountNoteAttachmentModelData,
  IndexedSpaceAccountNoteAttachmentSyncUpdateValue,
  NoteAttachmentHostingStatus,
  NoteAttachmentHostingFailureReason,
} from "@/store/note-attachments/types";
import { BatchUpdateSpaceAccountNoteAttachmentHostingOperation } from "@/store/sync/operations/note-attachments/BatchUpdateSpaceAccountNoteAttachmentHostingOperation";
import { SpaceAccountNoteAttachmentObservable } from "@/store/note-attachments/SpaceAccountNoteAttachmentObservable";
import { SpaceAccountNoteAttachmentIndexes } from "@/store/note-attachments/SpaceAccountNoteAttachmentIndexes";
import { NoteAttachmentsObservable } from "@/store/note-attachments/NoteAttachmentsObservable";
import { resolveSpaceAccountNoteAttachmentSyncModelUuid } from "@/store/note-attachments/resolveAttachmentUuid";
import { buildImageRenderInfo } from "@/store/note-attachments/buildImageRenderInfo";
import { getIsOnline } from "@/domains/network/status";
import {
  extractFileIdFromAttachmentUrl,
  mapContentUnderstandingState,
} from "@/store/note-attachments/mapContentUnderstandingState";

export class AppStoreSpaceAccountNoteAttachmentsStore extends BaseSyncModelStore<
  SpaceAccountNoteAttachmentObservable,
  SpaceAccountNoteAttachmentModelData
> {
  private noteAttachmentObservables = new Map<Uuid, NoteAttachmentsObservable>();

  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.SpaceAccountNoteAttachment, ...injectedDeps });

    makeObservable<
      AppStoreSpaceAccountNoteAttachmentsStore,
      "noteAttachmentObservables" | "getOrCreateNoteAttachmentsObservable"
    >(this, {
      createSyncModel: true,
      remoteTable: override,
      localTable: override,
      computeIndexes: override,
      resetSync: override,
      getAttachmentsForNote: action,
      writeHostingStatusOptimistically: false,
      getContentUnderstandingForNote: false,
      countPdfPagesProcessedSince: false,
      noteAttachmentObservables: observable,
      getOrCreateNoteAttachmentsObservable: action,
    });
  }

  createSyncModel(
    data: SyncUpdateValue<SpaceAccountNoteAttachmentModelData>
  ): SpaceAccountNoteAttachmentObservable {
    return new SpaceAccountNoteAttachmentObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  public get remoteTable() {
    return this.db.mappedTables[this.modelKind].remote as Table<
      SyncUpdateValue<SpaceAccountNoteAttachmentModelData>
    >;
  }

  public get localTable() {
    return this.db.mappedTables[this.modelKind]
      .local as Table<IndexedSpaceAccountNoteAttachmentSyncUpdateValue>;
  }

  public computeIndexes({
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<SpaceAccountNoteAttachmentModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<SpaceAccountNoteAttachmentModelData>[];
  }): Record<string, unknown> {
    return new SpaceAccountNoteAttachmentIndexes({ remoteData, optimisticUpdates }).indexes;
  }

  getAttachmentsForNote(noteId: Uuid): NoteAttachmentsObservable {
    return this.getOrCreateNoteAttachmentsObservable(noteId);
  }

  /**
   * FE-written `hosting_status` lifecycle for direct uploads (PENDING →
   * READY/FAILED). Dispatches the hosting BatchUpdate op, which both syncs the
   * FE-supplied fields to the BE and applies an optimistic update to the local
   * row.
   */
  async writeHostingStatusOptimistically({
    attachmentId,
    hostingStatus,
    servingUrl,
    hostingFailureReason,
  }: {
    attachmentId: Uuid;
    hostingStatus: NoteAttachmentHostingStatus | null;
    servingUrl?: string | null;
    hostingFailureReason?: NoteAttachmentHostingFailureReason | null;
  }): Promise<void> {
    await new BatchUpdateSpaceAccountNoteAttachmentHostingOperation({
      store: this.store,
      payload: {
        items: [
          {
            id: resolveSpaceAccountNoteAttachmentSyncModelUuid(attachmentId),
            hosting_status: hostingStatus,
            serving_url: servingUrl,
            hosting_failure_reason: hostingFailureReason,
          },
        ],
      },
    }).execute();
  }

  async getContentUnderstandingForNote(
    noteId: Uuid,
    { includeDisplayState }: { includeDisplayState?: boolean } = {}
  ): Promise<Record<string, MemCommonEditorContentUnderstandingInfo>> {
    const result: Record<string, MemCommonEditorContentUnderstandingInfo> = {};

    const records = await this.localTable.where("associated_note_id").equals(noteId).toArray();

    const isOnline = getIsOnline();

    for (const record of records) {
      const attachmentUrl = record.model_data.attachment_url;
      if (!attachmentUrl) continue;

      const isImage = record.model_data.mime_type?.startsWith("image/") ?? false;
      const cuInfo = mapContentUnderstandingState({
        extractionStatus: record.model_data.extraction_status,
        indexingStatus: record.model_data.indexing_status,
        processingInfo: record.model_data.processing_info,
        mediaKind: isImage ? "image" : "file",
        suggestedSearch: record.model_data.suggested_search,
        ocrMarkdown: record.model_data.ocr_markdown,
        includeDisplayState,
        isOnline,
      });

      const unwrappedUrl =
        assetProxyModule.extractOriginalUrlFromProxy({ proxiedUrl: attachmentUrl }) ??
        attachmentUrl;

      const attachmentId = record.model_data.associated_note_attachment_id;

      // Mint from OPFS before reading the verdict, so cached images render
      // offline on every surface — not just the editor's post-mount reaction.
      // Skip when already minted (a re-render fast path that avoids the index
      // read + LRU touch), and swallow failures so one torn/evicted entry can't
      // abort the whole note's seed build.
      if (isImage && !this.store.localBlobStore.getCachedObjectUrl(attachmentId)) {
        await this.store.localBlobStore.getBlobUrl(attachmentId).catch(() => null);
      }

      // Carry the render-resolver verdict on the seed too. The boot-seed path
      // REPLACES the editor's contentUnderstandingMap, so if the seed omitted
      // imageRender it would clobber the reaction's verdict on re-open (the
      // image would fall back to its raw src — broken offline).
      const imageRender = isImage
        ? buildImageRenderInfo({
            hostingStatus: record.model_data.hosting_status,
            servingUrl: record.model_data.serving_url,
            sourceUrl: unwrappedUrl,
            cachedBlobUrl: this.store.localBlobStore.getCachedObjectUrl(attachmentId),
            isLocalUpload: this.store.localBlobStore.isLocalUpload(attachmentId),
            isOnline,
          })
        : undefined;

      // An image with a render verdict but no extraction/indexing CU still needs
      // an entry, so don't skip purely on `!cuInfo`.
      const entry: MemCommonEditorContentUnderstandingInfo | undefined = imageRender
        ? { ...cuInfo, imageRender }
        : cuInfo;
      if (!entry) continue;

      // Primary key: UUID from the attachment URL (upload-time convention).
      const fileId = extractFileIdFromAttachmentUrl(attachmentUrl);
      if (fileId) result[fileId] = entry;

      // Secondary key: unwrapped attachment URL. Mirror the reaction in
      // mem-common-editor-store's CU dispatch — the seed path at note-boot
      // replaces `editor.storage.file.contentUnderstandingMap`, so this map
      // must also dual-key or the URL fallback gets wiped for old nodes.
      result[unwrappedUrl] = entry;
    }

    return result;
  }

  async countPdfPagesProcessedSince(periodStart: DateTime): Promise<number> {
    const periodStartISO = periodStart.toISO();
    const compoundIndex = "[mime_type+created_at+pages_processed+model_id]";

    // mime_type is pinned to "application/pdf" so non-PDF attachments (images
    // also receive a pages_processed value) are excluded from the PDF-page
    // paywall counter. .keys() reads only the index B-tree — no full record
    // reads.
    const keys = await this.localTable
      .where(compoundIndex)
      .between(
        ["application/pdf", periodStartISO, Dexie.minKey, Dexie.minKey],
        ["application/pdf", Dexie.maxKey, Dexie.maxKey, Dexie.maxKey]
      )
      .keys();

    // Each key is a tuple: [mime_type, created_at, pages_processed, model_id]
    return (keys as unknown as [string, string, number, string][]).reduce(
      (total, tuple) => total + (tuple[2] ?? 0),
      0
    );
  }

  override resetSync(): void {
    super.resetSync();
    this.noteAttachmentObservables.forEach(obs => obs.dispose());
    this.noteAttachmentObservables.clear();
  }

  private getOrCreateNoteAttachmentsObservable(noteId: Uuid): NoteAttachmentsObservable {
    let obs = this.noteAttachmentObservables.get(noteId);

    if (!obs) {
      obs = new NoteAttachmentsObservable({ store: this, noteId });
      this.noteAttachmentObservables.set(noteId, obs);
    }

    return obs;
  }
}
